import type {
  Dates,
  FileModelType,
  ID,
  Maybe,
  ModelType,
  Subtitles,
  Meta,
  BrandListItem,
  MetaSeo,
} from '@/types'
import type { MakeRequired } from '@/types/utilities'
/**
 * Catalogs
 * */
export type CatalogFormType = MakeRequired<
  Partial<Catalog>,
  'parameters' | 'merch'
>

export interface Catalog extends ModelType, Dates, Subtitles {
  name: Maybe<string>
  code: Maybe<string>
  title: Maybe<string>
  parameters: {
    show_filters: boolean
    show_sort: boolean
    show_discounted: DiscountedOptions
    show_presets: boolean
    new_before: number
    by_photo_dates: boolean
    by_last_days: number
  }
  categories: ID[]
  brands: ID[]
  /**
   * Single product
   * */
  unions: Union[]
  /**
   * Merchandising
   * */
  merch: {
    new_products_first: boolean
    type: 'groups' | 'sort'
    groups: CatalogSortByItem[]
    sort: ProductsSortOptions
  }

  seo: MetaSeo

  products: {
    active: number
    total: number
  }
}

export enum DiscountedOptions {
  Hide = 'hide',
  Show = 'show',
  Only = 'only',
}
/**
 * Catalogs: merch
 * */
export interface CatalogSortByItem extends Subtitles {
  id: Maybe<Category['id']>
  name: Maybe<Category['name']>
  sort: ProductsSortOptions | 'groups'

  brands: AvailableBrandModels[]
  colors: ProductColor['color'][]
}
export interface AvailableBrandModels {
  name: NonNullable<BrandListItem['name']>
  models: SingleProductModel['model'][]
}
export type SortableAvailableBrandModels = {
  id: AvailableBrandModels['name']
  name: AvailableBrandModels['name']
  category: NonNullable<Category['name']>
  children: AvailableBrandModels['models']
}
export type SortableColor = {
  id: string
  name: string
}
export enum ProductsSortOptions {
  StockAsc = 'stock_asc',
  StockDesc = 'stock_desc',
  PriceAsc = 'price_asc',
  PriceDesc = 'price_desc',
  DiscountAsc = 'discount_asc',
  DiscountDesc = 'discount_desc',
}
/**
 * Catalogs: categories
 * */
export interface Category extends ModelType {
  code: Maybe<string>
  description: Maybe<string>
  name: Maybe<string>
  brands?: BrandListItem[] // TODO: а этот массив брендов вообще где?
  colors: ProductColor['color'][]
  models: AvailableBrandModels[]
}
/**
 * Catalogs: unions
 * */
export interface SingleProductModel {
  model: string
  brand: string
  category: string

  stock: number
  variants_count: number
}
export interface Union
  extends Pick<SingleProductModel, 'model' | 'brand' | 'category'> {
  colors?: ProductColor['color'][]
}
export interface ProductColor
  extends Pick<SingleProductModel, 'model' | 'brand' | 'category' | 'stock'> {
  color: string
  images: FileModelType[]

  id: ProductColor['color']
  columnTitle?: string
  active: boolean
}
/**
 * Catalogs: filters, labels
 * */
export interface Filter extends ModelType, Subtitles, Dates {
  name: Maybe<string>
  source: Maybe<string>
  option_id: Maybe<number>
  function: Maybe<string>
  type?: FilterType

  values?: Maybe<string[] | string>
  values_applicable?: Maybe<string[]>

  editable: boolean

  locations: FilterLocation[]
  hidden?: boolean
}
export interface SelectedFilter {
  filter_id: Filter['id']
  values: string[] | [string] | [number, number] | ID[]
}
export interface Label extends ModelType, Subtitles, Dates {
  name: Maybe<string>
  type: string
}
export type FilterLocation = 'products' | 'presets' | 'storefront'
export type FilterType = 'multiselect' | 'range' | 'boolean' | 'string'
export type FilterUiValues =
  | string
  | boolean
  | [number, number]
  | Record<string, any>
/**
 * Catalogs: presets
 * */
export interface Preset extends Omit<ModelType, 'sort'>, Subtitles {
  catalog_id: Maybe<Catalog['id']>
  name: Maybe<string>
  parameters: {
    all_products: boolean
    auto: boolean
    min_products: number
  }
  products: {
    active: number
    total: number
  }
  filters: SelectedFilter[]
  images: FileModelType[]
  unions: string[]
  code: Maybe<string>
}
export type PresetForm = MakeRequired<Partial<Preset>, 'parameters' | 'images'>
export type PresetsMeta = MakeRequired<Meta<Preset>, 'total' | 'active'>
